import { Copy } from 'lucide-react';
import { type PropsWithChildren, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useTranslation } from 'react-i18next';

type ChatAiCodeBlockProps = PropsWithChildren<{
  className?: string;
}>;

export function ChatAiCodeBlock({ children, className }: ChatAiCodeBlockProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const language = className?.replace('language-', '') || 'text';
  const codeContent = String(children).replace(/\n$/, '');

  const handleCopy = async () => {
    await navigator.clipboard.writeText(codeContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative bg-gray-100 rounded-lg mb-4 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
        <span className="text-xs font-medium text-gray-600">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
          title="Copy code"
          data-testid="copy-button"
        >
          <Copy size={12} />
          {copied ? t('buttons.copied') : t('buttons.copy')}
        </button>
      </div>

      <SyntaxHighlighter
        language={language}
        style={oneLight}
        customStyle={{
          margin: 0,
          backgroundColor: 'transparent',
        }}
        codeTagProps={{}}
      >
        {codeContent}
      </SyntaxHighlighter>
    </div>
  );
}
